import { Fragment, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type PermissionRow, type RoleRow } from '../lib/api'
import { Empty, ErrorNote } from '../components/Page'
import { Card, CardNote, ConsoleHead, Square } from '../components/admin/Console'
import { useSession } from '../lib/session'

/**
 * Guest first, then the ordinary reading roles, then the staff ones: the
 * matrix reads as a ladder of trust rather than as an alphabet. A role the
 * catalogue grew since is appended rather than dropped.
 */
const RANK = ['guest', 'reader', 'researcher', 'librarian', 'editor', 'admin']

/**
 * Roles and permissions, as one matrix.
 *
 * Permissions are database-driven: a role is a composition, nothing in the
 * application tests a role by name, and a role can be recomposed without a
 * deploy. The matrix is the honest shape for that, because the question an
 * administrator actually has is "who can do this", which a per-role form
 * cannot answer without opening six forms.
 */
export function RolesPage() {
  const queryClient = useQueryClient()
  const { can } = useSession()
  const editable = can('admin.roles.manage')

  const roles = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => api.get<RoleRow[]>('/admin/roles'),
  })

  const permissions = useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: () => api.get<PermissionRow[]>('/admin/permissions'),
  })

  const save = useMutation({
    mutationFn: (input: { id: string; permissions: string[] }) =>
      api.patch<RoleRow>(`/admin/roles/${input.id}`, { permissions: input.permissions }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] }),
  })

  const create = useMutation({
    mutationFn: (input: { key: string; name: string }) =>
      api.post<RoleRow>('/admin/roles', { ...input, permissions: [] }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] }),
  })

  const ordered = useMemo(() => {
    return [...(roles.data ?? [])].sort((a, b) => {
      const left = RANK.indexOf(a.key)
      const right = RANK.indexOf(b.key)
      return (left < 0 ? RANK.length : left) - (right < 0 ? RANK.length : right)
    })
  }, [roles.data])

  const rows = useMemo(() => {
    return [...(permissions.data ?? [])].sort(
      (a, b) => a.category.localeCompare(b.category) || a.key.localeCompare(b.key),
    )
  }, [permissions.data])

  function toggle(role: RoleRow, key: string) {
    if (!editable || save.isPending) return
    const next = role.permissions.includes(key)
      ? role.permissions.filter((entry) => entry !== key)
      : [...role.permissions, key]
    save.mutate({ id: role.id, permissions: next })
  }

  return (
    <>
      <ConsoleHead
        title="Roles and permissions"
        note={`${rows.length} permissions · ${ordered.length} roles`}
        actions={
          editable && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                const key = window.prompt('Role key (lowercase, e.g. visiting_fellow)')
                if (!key) return
                const name = window.prompt('Display name', key) ?? key
                create.mutate({ key, name })
              }}
            >
              New role
            </button>
          )
        }
      />

      <ErrorNote error={save.error ?? create.error} />

      <Card className="max-w-[980px]">
        {roles.isLoading || permissions.isLoading ? (
          <Empty>Reading the catalogue.</Empty>
        ) : !rows.length ? (
          <Empty>No permissions are registered. The bootstrap inserts the catalogue on start.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <div
              className="grid min-w-[720px]"
              style={{ gridTemplateColumns: `1.55fr repeat(${ordered.length}, minmax(72px, 1fr))` }}
            >
              <div className="border-b border-n300 bg-surface px-2.5 py-2 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink2">
                Permission
              </div>
              {ordered.map((role) => (
                <div
                  key={role.id}
                  title={`${role.name} · ${role.user_count} account(s)`}
                  className="truncate border-b border-n300 bg-surface px-2.5 py-2 text-center font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-ink2"
                >
                  {role.key}
                </div>
              ))}

              {rows.map((permission) => (
                <Fragment key={permission.key}>
                  <div
                    className="flex items-center border-b border-n200 px-2.5 py-[6px] font-mono text-[11.5px]"
                    title={permission.description ?? undefined}
                  >
                    {permission.key}
                  </div>
                  {ordered.map((role) => {
                    const on = role.permissions.includes(permission.key)
                    const label = `${permission.key} for ${role.key}: ${on ? 'granted' : 'not granted'}`
                    return (
                      <div
                        key={role.id + permission.key}
                        className="flex items-center justify-center border-b border-l border-n200 px-2.5 py-[6px]"
                      >
                        {editable ? (
                          <button
                            type="button"
                            aria-pressed={on}
                            aria-label={label}
                            title={label}
                            disabled={save.isPending}
                            onClick={() => toggle(role, permission.key)}
                            className="flex items-center rounded-[2px] p-[3px] disabled:opacity-45"
                          >
                            <Square on={on} label={label} />
                          </button>
                        ) : (
                          <Square on={on} label={label} />
                        )}
                      </div>
                    )
                  })}
                </Fragment>
              ))}
            </div>
          </div>
        )}
      </Card>

      <CardNote>
        BOOTSTRAP RECONCILES THE CATALOGUE ON EVERY START: IT INSERTS WHAT IS MISSING
        <br />
        AND NEVER MODIFIES WHAT EXISTS. A REMOVED PERMISSION STAYS REMOVED.
      </CardNote>

      {!editable && (
        <p className="mt-3 max-w-[70ch] text-[15px] text-ink2">
          You can read this matrix but not change it. Composing a role needs{' '}
          <span className="font-mono text-[13px]">admin.roles.manage</span>.
        </p>
      )}
    </>
  )
}
